/**
 * TestMarking — per-student test marking with auto-scoring (Phase 118.B).
 *
 * Route: /tests/:testId/mark
 *
 * Flow:
 *   - Teacher loads the page → sees a list of existing student
 *     results plus an "Add student" pane
 *   - Picks a student (or types a new name)
 *   - For each question, enters the student's answer
 *     - Objective types (MC / TF / matching / labeling) score
 *       automatically as the teacher inputs the answer
 *     - Written types accept a points-earned input (manual marking
 *       for now; AI-assist lands in Phase 118.B.2)
 *   - Totals + per-category breakdown update live
 *   - Save → backend recomputes (defensively) + persists; the
 *     Gradebook assessment row picks the result up via `linked_test_id`
 *
 * Crucially: NO Ontario achievement level field. Teacher reads the
 * raw + % + per-category breakdown and assigns the level themselves
 * in the Gradebook. The platform never converts arithmetic to a
 * letter / number grade.
 */

import { useEffect, useMemo, useState } from "react";
import { Navigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Plus, Save, Sparkles, UserPlus } from "lucide-react";
import AppShell from "../components/shell/AppShell";
import { useAuth } from "../contexts/AuthContext";
import {
  ACHIEVEMENT_CATEGORIES,
  QUESTION_TYPES,
  getTest,
  listTestResults,
  markTest,
  suggestScore,
} from "../lib/tests";

const _qTypeLabel = (key) => (QUESTION_TYPES.find((t) => t.key === key) || {}).label || key;

// ----------------------------------------------------------------------------
//  Per-question input — mirrors the editor but accepts a student
//  answer rather than the correct one. Computes live points.
// ----------------------------------------------------------------------------

const QuestionMarkRow = ({ q, mark, onChange, onSuggest, suggesting, suggestion, onAcceptSuggestion, onDismissSuggestion }) => {
  const set = (patch) => onChange({ ...mark, ...patch });
  const autoEarned = useMemo(() => {
    if (q.type === "multiple_choice") {
      if (mark.student_answer_index == null) return null;
      return mark.student_answer_index === q.correct_option_index ? q.points : 0;
    }
    if (q.type === "true_false") {
      if (mark.student_answer_bool == null) return null;
      return mark.student_answer_bool === q.correct_bool ? q.points : 0;
    }
    if (q.type === "matching") {
      const pairs = q.pairs || [];
      if (pairs.length === 0) return 0;
      const matches = mark.student_matches || {};
      const correct = pairs.filter((p) => matches[p.left] === p.right).length;
      return Math.round((correct / pairs.length) * q.points * 100) / 100;
    }
    if (q.type === "labeling") {
      const labels = q.labels || [];
      if (labels.length === 0) return 0;
      const sl = mark.student_labels || [];
      const n = Math.min(labels.length, sl.length);
      let correct = 0;
      for (let i = 0; i < n; i += 1) {
        if ((sl[i] || "").trim() === (labels[i] || "").trim()) correct += 1;
      }
      return Math.round((correct / labels.length) * q.points * 100) / 100;
    }
    return null;
  }, [q, mark]);
  const displayedEarned = autoEarned == null ? mark.points_earned ?? 0 : autoEarned;

  return (
    <div className="ae-card p-4 space-y-3" data-testid={`mark-question-${q.id}`}>
      <div className="flex items-start justify-between flex-wrap gap-2">
        <p className="font-extrabold text-[11px] uppercase tracking-[0.18em] text-[#4a6984]/75">
          {_qTypeLabel(q.type)} · {q.achievement_category} · {q.points} {q.points === 1 ? "mark" : "marks"}
        </p>
        <span
          className="ae-pill bg-[#96ded1]/30 text-[#1c2d3d]"
          data-testid={`mark-question-${q.id}-earned`}
        >
          {displayedEarned} / {q.points}
        </span>
      </div>
      <p className="text-sm text-[#1c2d3d] leading-snug">{q.prompt}</p>
      {q.type === "multiple_choice" ? (
        <div className="space-y-1">
          {(q.options || []).map((opt, i) => (
            <label key={i} className="flex items-center gap-2 text-sm text-[#1c2d3d]">
              <input
                type="radio"
                name={`mc-${q.id}`}
                checked={mark.student_answer_index === i}
                onChange={() => set({ student_answer_index: i })}
              />
              <span className={i === q.correct_option_index ? "font-extrabold" : ""}>
                {String.fromCharCode(65 + i)}) {opt}
              </span>
            </label>
          ))}
        </div>
      ) : null}
      {q.type === "true_false" ? (
        <div className="flex gap-3 text-sm font-bold text-[#1c2d3d]">
          {[true, false].map((v) => (
            <label key={String(v)} className="inline-flex items-center gap-1">
              <input
                type="radio"
                checked={mark.student_answer_bool === v}
                onChange={() => set({ student_answer_bool: v })}
              />
              {v ? "True" : "False"}
            </label>
          ))}
        </div>
      ) : null}
      {q.type === "matching" ? (
        <div className="space-y-1.5">
          {(q.pairs || []).map((p) => {
            const allRights = (q.pairs || []).map((pp) => pp.right);
            return (
              <div key={p.left} className="flex items-center gap-2 text-sm">
                <span className="flex-1 text-[#1c2d3d]">{p.left}</span>
                <select
                  value={(mark.student_matches || {})[p.left] || ""}
                  onChange={(e) =>
                    set({
                      student_matches: {
                        ...(mark.student_matches || {}),
                        [p.left]: e.target.value,
                      },
                    })
                  }
                  className="h-9 rounded-md border border-[#4a6984]/25 bg-white px-2 text-xs font-bold text-[#1c2d3d]"
                >
                  <option value="">— pick —</option>
                  {allRights.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>
            );
          })}
        </div>
      ) : null}
      {q.type === "labeling" ? (
        <div className="space-y-1.5">
          {(q.labels || []).map((label, i) => (
            <div key={i} className="flex items-center gap-2 text-sm">
              <span className="text-[10px] uppercase font-bold tracking-[0.18em] text-[#4a6984]/70 w-12">
                Slot {i + 1}
              </span>
              <input
                type="text"
                value={(mark.student_labels || [])[i] || ""}
                onChange={(e) => {
                  const next = [...(mark.student_labels || [])];
                  while (next.length <= i) next.push("");
                  next[i] = e.target.value;
                  set({ student_labels: next });
                }}
                placeholder={`Correct: ${label}`}
                className="flex-1 h-9 rounded-md border border-[#4a6984]/25 bg-white px-3 text-sm text-[#1c2d3d]"
              />
            </div>
          ))}
        </div>
      ) : null}
      {(q.type === "short_answer" || q.type === "long_answer") ? (
        <div className="space-y-2">
          <textarea
            value={mark.student_answer || ""}
            onChange={(e) => set({ student_answer: e.target.value })}
            placeholder="What did the student write…"
            rows={q.type === "long_answer" ? 4 : 2}
            className="w-full rounded-md border border-[#4a6984]/25 bg-white px-3 py-2 text-sm resize-none"
            data-testid={`mark-question-${q.id}-answer-input`}
          />
          <div className="flex items-center justify-between gap-2 text-xs flex-wrap">
            <p className="text-[#4a6984]/75 italic flex-1 min-w-[180px]">
              Marking guide: {q.marking_guide || "—"}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => onSuggest && onSuggest(q.id, mark.student_answer || "")}
                disabled={suggesting || !(mark.student_answer || "").trim()}
                title={!(mark.student_answer || "").trim() ? "Type the student's answer first" : "Get a suggested score"}
                className="inline-flex items-center gap-1 h-9 px-2.5 rounded-md border border-[#96ded1] bg-[#96ded1]/15 text-[11px] font-bold uppercase tracking-[0.12em] text-[#1c2d3d] hover:bg-[#96ded1]/30 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid={`mark-question-${q.id}-suggest-btn`}
              >
                {suggesting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                {suggesting ? "Thinking…" : "Suggest score"}
              </button>
              <label className="text-[10px] uppercase font-bold tracking-[0.18em] text-[#4a6984]/70">
                Points
              </label>
              <input
                type="number"
                min="0"
                max={q.points}
                step="0.5"
                value={mark.points_earned ?? 0}
                onChange={(e) =>
                  set({ points_earned: Math.max(0, Math.min(q.points, parseFloat(e.target.value || "0"))) })
                }
                className="w-20 h-9 rounded-md border border-[#4a6984]/25 bg-white px-2 text-center text-sm font-bold text-[#1c2d3d]"
                data-testid={`mark-question-${q.id}-points-input`}
              />
            </div>
          </div>
          {suggestion ? (
            <div
              className="rounded-md border border-[#96ded1] bg-[#96ded1]/15 p-3 text-xs text-[#1c2d3d] space-y-2"
              data-testid={`mark-question-${q.id}-suggestion`}
            >
              <p className="font-bold uppercase tracking-[0.12em] text-[10px] text-[#4a6984] inline-flex items-center gap-1">
                <Sparkles className="h-3 w-3" />
                Suggested score: {suggestion.suggested_points} / {suggestion.max_points}
              </p>
              <p className="leading-snug">{suggestion.rationale}</p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => onAcceptSuggestion && onAcceptSuggestion(q.id, suggestion.suggested_points)}
                  className="inline-flex items-center h-7 px-2.5 rounded-md bg-[#1c2d3d] text-white text-[11px] font-bold hover:bg-[#0e1a25]"
                  data-testid={`mark-question-${q.id}-suggestion-accept`}
                >
                  Accept score
                </button>
                <button
                  type="button"
                  onClick={() => onDismissSuggestion && onDismissSuggestion(q.id)}
                  className="inline-flex items-center h-7 px-2.5 rounded-md border border-[#4a6984]/25 bg-white text-[11px] font-bold text-[#4a6984] hover:bg-[#f4f5f7]"
                  data-testid={`mark-question-${q.id}-suggestion-dismiss`}
                >
                  Dismiss
                </button>
                <span className="text-[10px] italic text-[#4a6984]/75 ml-auto">
                  Your call. Suggestions are not auto-applied.
                </span>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

// ----------------------------------------------------------------------------
//  Page
// ----------------------------------------------------------------------------

const _initMarks = (test, activeResult) => {
  if (!test) return {};
  const next = {};
  const existingByQ = {};
  if (activeResult) {
    for (const m of (activeResult.marks || [])) existingByQ[m.question_id] = m;
  }
  for (const q of (test.questions || [])) {
    next[q.id] = existingByQ[q.id] || {
      question_id: q.id,
      points_earned: 0,
      points_possible: q.points,
    };
  }
  return next;
};

const TestMarking = () => {
  const { user, loading: authLoading } = useAuth();
  const { testId } = useParams();
  const [test, setTest] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeResultId, setActiveResultId] = useState(null);
  const [studentName, setStudentName] = useState("");
  const [marks, setMarks] = useState({});  // {question_id: TestQuestionMark}
  const [saving, setSaving] = useState(false);
  // Phase 118.B.2 — per-question Claude marking suggestions.
  const [suggestionsByQ, setSuggestionsByQ] = useState({});  // {question_id: {suggested_points, rationale, max_points}}
  const [suggestingQ, setSuggestingQ] = useState(null);  // question_id currently fetching

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [t, rs] = await Promise.all([getTest(testId), listTestResults(testId)]);
        if (cancelled) return;
        setTest(t);
        setResults(rs);
        setMarks(_initMarks(t, null));
        setStudentName("");
        setLoading(false);
      } catch (_err) {
        if (cancelled) return;
        toast.error("Couldn't load that test");
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [testId]);

  const selectStudent = (resultId) => {
    setActiveResultId(resultId);
    const activeResult = resultId ? results.find((r) => r.id === resultId) : null;
    setMarks(_initMarks(test, activeResult));
    setStudentName(activeResult?.student_name || "");
    setSuggestionsByQ({});  // clear stale AI suggestions when switching students
  };

  const handleSuggestScore = async (questionId, studentAnswer) => {
    if (!studentAnswer.trim()) {
      toast.error("Add the student's answer first.");
      return;
    }
    setSuggestingQ(questionId);
    try {
      const out = await suggestScore(testId, {
        question_id: questionId,
        student_answer: studentAnswer,
      });
      setSuggestionsByQ((prev) => ({ ...prev, [questionId]: out }));
    } catch (e) {
      toast.error("Couldn't fetch suggestion", { description: e?.response?.data?.detail || e?.message });
    } finally {
      setSuggestingQ(null);
    }
  };

  const handleAcceptSuggestion = (questionId, suggestedPoints) => {
    setMarks((prev) => ({
      ...prev,
      [questionId]: {
        ...(prev[questionId] || { question_id: questionId, points_possible: 0 }),
        points_earned: suggestedPoints,
      },
    }));
    setSuggestionsByQ((prev) => {
      const next = { ...prev };
      delete next[questionId];
      return next;
    });
    toast.success(`Applied score: ${suggestedPoints}`);
  };

  const handleDismissSuggestion = (questionId) => {
    setSuggestionsByQ((prev) => {
      const next = { ...prev };
      delete next[questionId];
      return next;
    });
  };

  const liveTotals = useMemo(() => {
    if (!test) return { raw: 0, possible: 0, percent: 0, by: {} };
    let raw = 0;
    let possible = 0;
    const by = {};
    ACHIEVEMENT_CATEGORIES.forEach((c) => { by[c] = { earned: 0, possible: 0 }; });
    for (const q of (test.questions || [])) {
      const m = marks[q.id] || {};
      // Replicate the same auto-score the backend will compute so the
      // displayed total never diverges from the persisted one.
      let earned = 0;
      if (q.type === "multiple_choice") {
        if (m.student_answer_index === q.correct_option_index) earned = q.points;
      } else if (q.type === "true_false") {
        if (m.student_answer_bool === q.correct_bool) earned = q.points;
      } else if (q.type === "matching") {
        const pairs = q.pairs || [];
        if (pairs.length) {
          const correct = pairs.filter((p) => (m.student_matches || {})[p.left] === p.right).length;
          earned = Math.round((correct / pairs.length) * q.points * 100) / 100;
        }
      } else if (q.type === "labeling") {
        const labels = q.labels || [];
        if (labels.length) {
          const sl = m.student_labels || [];
          const n = Math.min(labels.length, sl.length);
          let correct = 0;
          for (let i = 0; i < n; i += 1) {
            if ((sl[i] || "").trim() === (labels[i] || "").trim()) correct += 1;
          }
          earned = Math.round((correct / labels.length) * q.points * 100) / 100;
        }
      } else {
        earned = Math.max(0, Math.min(q.points, Number(m.points_earned) || 0));
      }
      raw += earned;
      possible += q.points;
      const cat = q.achievement_category in by ? q.achievement_category : "Knowledge";
      by[cat].earned += earned;
      by[cat].possible += q.points;
    }
    const percent = possible ? Math.round((raw / possible) * 1000) / 10 : 0;
    return { raw: Math.round(raw * 100) / 100, possible, percent, by };
  }, [test, marks]);

  const handleSave = async () => {
    if (!studentName.trim()) {
      toast.error("Add the student's name first.");
      return;
    }
    setSaving(true);
    try {
      const body = {
        student_name: studentName.trim(),
        marks: Object.values(marks),
        result_id: activeResultId || undefined,
      };
      const saved = await markTest(testId, body);
      toast.success(`Saved · ${saved.student_name}: ${saved.raw_total}/${saved.possible_total} (${saved.percent}%)`);
      const fresh = await listTestResults(testId);
      setResults(fresh);
      setActiveResultId(saved.id);
      const refreshed = fresh.find((r) => r.id === saved.id) || saved;
      setMarks(_initMarks(test, refreshed));
      setStudentName(refreshed.student_name || "");
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e?.message });
    } finally {
      setSaving(false);
    }
  };

  if (!authLoading && !user) return <Navigate to="/" replace />;

  return (
    <AppShell title="Mark students" subtitle={test?.title || ""}>
      <div className="bg-[#f4f5f7] min-h-full">
        <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-5">
          {/* Sidebar */}
          <aside className="ae-card p-3 self-start sticky top-3" data-testid="test-marking-sidebar">
            <Link
              to={`/tests/${testId}`}
              className="inline-flex items-center gap-1.5 text-xs font-bold text-[#4a6984] hover:text-[#1c2d3d] mb-3"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to test
            </Link>
            <p className="font-extrabold text-[11px] uppercase tracking-[0.18em] text-[#4a6984]/70 px-1 pb-2">
              Students ({results.length})
            </p>
            <button
              type="button"
              onClick={() => selectStudent(null)}
              className={`w-full text-left px-3 py-2 rounded-md text-xs font-bold mb-1 ${!activeResultId ? "bg-[#96ded1]/25 text-[#1c2d3d]" : "text-[#4a6984] hover:bg-[#f4f5f7]"}`}
              data-testid="test-marking-new-student-btn"
            >
              <span className="inline-flex items-center gap-1.5">
                <UserPlus className="h-3.5 w-3.5" />
                + New student
              </span>
            </button>
            {results.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => selectStudent(r.id)}
                className={`w-full text-left px-3 py-2 rounded-md text-xs ${activeResultId === r.id ? "bg-[#96ded1]/25 text-[#1c2d3d] font-bold" : "text-[#4a6984] hover:bg-[#f4f5f7]"}`}
                data-testid={`test-marking-student-${r.id}`}
              >
                <p className="font-bold truncate">{r.student_name}</p>
                <p className="text-[10px] text-[#4a6984]/70">{r.raw_total}/{r.possible_total} · {r.percent}%</p>
              </button>
            ))}
          </aside>

          {/* Main marking pane */}
          <section className="space-y-4">
            {loading ? (
              <div className="ae-card p-10 flex items-center justify-center text-[#4a6984]">
                <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading test…
              </div>
            ) : !test ? null : (
              <>
                {/* Student name + totals */}
                <div className="ae-card p-4">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-[240px]">
                      <label className="block text-[10px] uppercase font-bold tracking-[0.18em] text-[#4a6984]/70 mb-1">
                        Student name
                      </label>
                      <input
                        type="text"
                        value={studentName}
                        onChange={(e) => setStudentName(e.target.value)}
                        placeholder="e.g. Avery T."
                        className="w-full h-10 rounded-md border border-[#4a6984]/25 bg-white px-3 text-base font-bold text-[#1c2d3d] focus:outline-none focus:ring-2 focus:ring-[#96ded1]"
                        data-testid="test-marking-student-name"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleSave}
                      disabled={saving}
                      className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md bg-[#1c2d3d] text-white text-sm font-bold hover:bg-[#0e1a25] disabled:opacity-50"
                      data-testid="test-marking-save-btn"
                    >
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                      {saving ? "Saving…" : "Save mark"}
                    </button>
                  </div>
                  <div className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="test-marking-totals">
                    <div className="ae-pill bg-[#1c2d3d] text-white">
                      <strong>{liveTotals.raw}</strong> / {liveTotals.possible}
                      <span className="ml-1 opacity-70">raw</span>
                    </div>
                    <div className="ae-pill bg-[#96ded1]/40 text-[#1c2d3d]">
                      <strong>{liveTotals.percent}%</strong>
                    </div>
                    {ACHIEVEMENT_CATEGORIES.map((c) => (
                      <div key={c} className="ae-pill bg-[#f4f5f7] text-[#1c2d3d]">
                        <span className="font-bold">{c[0]}</span> {liveTotals.by[c].earned}/{liveTotals.by[c].possible}
                      </div>
                    ))}
                  </div>
                  <p className="mt-3 text-[11px] italic text-[#4a6984]/75">
                    The platform records raw and percentage. The Ontario achievement level is your professional judgement — enter it in the Gradebook when you mark this assessment.
                  </p>
                </div>

                {(test.questions || []).map((q) => (
                  <QuestionMarkRow
                    key={q.id}
                    q={q}
                    mark={marks[q.id] || { question_id: q.id, points_earned: 0, points_possible: q.points }}
                    onChange={(m) => setMarks((prev) => ({ ...prev, [q.id]: m }))}
                    onSuggest={handleSuggestScore}
                    suggesting={suggestingQ === q.id}
                    suggestion={suggestionsByQ[q.id] || null}
                    onAcceptSuggestion={handleAcceptSuggestion}
                    onDismissSuggestion={handleDismissSuggestion}
                  />
                ))}
              </>
            )}
          </section>
        </main>
      </div>
    </AppShell>
  );
};

export default TestMarking;
